import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * Deutschsprachiger Leitfaden (locale: 'de', logpulse.de): NIS2 und das
 * deutsche Umsetzungsgesetz. Bewusst ein eigenständiges lokales Stück
 * (NIS2UmsuCG, BSIG, BSI, KRITIS, deutsche Sektoren und Bußgelder), keine
 * Übersetzung des englischen NIS2-Leitfadens — der behandelt die Richtlinie
 * selbst und bleibt über die Related-Links als Vertiefung erreichbar.
 */
export function Nis2UmsetzungsgesetzGuideBody() {
  return (
    <>
      <Lead>
        NIS2 ist die europäische Cybersicherheitsrichtlinie, die in Deutschland
        über das NIS2-Umsetzungsgesetz in Kraft gesetzt wird. Schätzungen
        zufolge sind rund 30.000 deutsche Unternehmen betroffen, ein Vielfaches
        der bisherigen KRITIS-Regulierung, darunter viele Mittelständler, die
        nie zuvor unter Cybersicherheitsrecht fielen. Dieser Leitfaden erklärt,
        wer betroffen ist, was das Gesetz verlangt, und was das konkret für
        Ihr Logging und Monitoring bedeutet.
      </Lead>

      <GuideSection id="was-ist-nis2" title="Von der Richtlinie zum NIS2-Umsetzungsgesetz">
        <P>
          NIS2 (Richtlinie (EU) 2022/2555) ist die Nachfolgerin der ersten
          NIS-Richtlinie. Deutschland setzt sie mit dem{' '}
          <strong>
            NIS2-Umsetzungs- und Cybersicherheitsstärkungsgesetz (NIS2UmsuCG)
          </strong>{' '}
          um, im Kern einer umfassenden Novelle des BSI-Gesetzes (BSIG). Das
          Gesetz wurde im Dezember 2025 verkündet und ist{' '}
          <strong>seit dem 6. Dezember 2025 in Kraft</strong>, ohne
          Übergangsfrist für die Sicherheitsmaßnahmen.
        </P>
        <P>
          Das heißt: Die Pflichten gelten jetzt. Die dreimonatige
          Registrierungsfrist beim BSI ist im März 2026 abgelaufen; wer
          betroffen ist und sich noch nicht registriert oder die Maßnahmen
          noch nicht umgesetzt hat, sollte das nicht auf die lange Bank
          schieben, sondern priorisiert nachholen.
        </P>
        <P>
          Die größte Verschiebung gegenüber der bisherigen Rechtslage: Die
          KRITIS-Regulierung erfasste einzelne kritische Anlagen ab
          Schwellenwerten. NIS2 erfasst <strong>ganze Sektoren nach
          Unternehmensgröße</strong> und zieht damit einen erheblichen Teil
          des deutschen Mittelstands in die Pflicht.
        </P>
      </GuideSection>

      <GuideSection id="bin-ich-betroffen" title="Bin ich betroffen?">
        <P>Das Gesetz kennt zwei Kategorien mit unterschiedlicher Aufsicht und unterschiedlichen Bußgeldern:</P>
        <UL>
          <LI>
            <strong>Besonders wichtige Einrichtungen</strong>: große
            Unternehmen in Sektoren wie Energie, Transport, Bankwesen,
            Finanzmarktinfrastruktur, Gesundheit, Trink- und Abwasser,
            digitale Infrastruktur, öffentliche Verwaltung und Weltraum,
            sowie KRITIS-Betreiber.
          </LI>
          <LI>
            <strong>Wichtige Einrichtungen</strong>: mittlere und große
            Unternehmen unter anderem in Post- und Kurierdiensten,
            Abfallwirtschaft, Chemie, Lebensmitteln, verarbeitendem Gewerbe
            (darunter Medizinprodukte und Elektronik), bei digitalen
            Diensten und in der Forschung.
          </LI>
        </UL>
        <P>
          Die Größenschwelle liegt in der Basis bei etwa 50 Beschäftigten oder
          10 Millionen Euro Jahresumsatz. Kleinere Unternehmen können dennoch
          betroffen sein, etwa als einziger Anbieter eines wesentlichen
          Dienstes. Es gilt außerdem eine{' '}
          <strong>Registrierungspflicht beim BSI</strong>; die reguläre Frist
          dafür (drei Monate nach Inkrafttreten) ist im März 2026 abgelaufen,
          Nachzügler sollten die Registrierung umgehend nachholen.
        </P>
        <P>
          Im Zweifel: Das BSI stellt eine offizielle{' '}
          <strong>NIS-2-Betroffenheitsprüfung</strong> online bereit, mit der
          Sie in wenigen Minuten prüfen, ob und in welcher Kategorie Ihr
          Unternehmen betroffen ist.
        </P>
      </GuideSection>

      <GuideSection id="pflichten" title="Die Pflichten: Risikomanagement nach Stand der Technik">
        <P>
          Kern des Gesetzes sind verhältnismäßige technische und
          organisatorische Maßnahmen nach dem Stand der Technik, um die
          Risiken für Ihre Netz- und Informationssysteme zu beherrschen. Die
          Richtlinie nennt ausdrücklich unter anderem:
        </P>
        <UL>
          <LI>Risikoanalyse und Sicherheitskonzepte;</LI>
          <LI>
            <strong>Bewältigung von Sicherheitsvorfällen</strong>: erkennen,
            analysieren, eindämmen, wiederherstellen, und all das belegen
            können;
          </LI>
          <LI>Betriebskontinuität, Backups und Krisenmanagement;</LI>
          <LI>Sicherheit der Lieferkette;</LI>
          <LI>Sicherheit bei Entwicklung, Beschaffung und Wartung;</LI>
          <LI>Verfahren zur Messung der Wirksamkeit der Maßnahmen;</LI>
          <LI>Cyberhygiene und Schulungen;</LI>
          <LI>Kryptografie und, wo angemessen, Verschlüsselung;</LI>
          <LI>Zugriffskontrolle, Personalsicherheit und Asset-Management;</LI>
          <LI>Multi-Faktor-Authentifizierung und gesicherte Kommunikation.</LI>
        </UL>
        <P>
          Neu ist die <strong>persönliche Verantwortung der
          Geschäftsleitung</strong>: Sie muss die Maßnahmen billigen, ihre
          Umsetzung überwachen und regelmäßig an Schulungen teilnehmen. Bei
          grober Pflichtverletzung droht die persönliche Haftung.
        </P>
        <P>
          Für <strong>KRITIS-Betreiber</strong> gilt zusätzlich bereits heute
          die Pflicht zu Systemen zur Angriffserkennung (§ 8a BSIG): eine
          ausdrückliche gesetzliche Anforderung an Protokollierung, Detektion
          und Reaktion, die das BSI in einer Orientierungshilfe konkretisiert
          hat.
        </P>
      </GuideSection>

      <GuideSection id="meldepflicht" title="Die Meldepflicht: 24 Stunden, 72 Stunden, ein Monat">
        <P>Erhebliche Sicherheitsvorfälle melden Sie an das BSI, in drei Stufen:</P>
        <OL>
          <LI>
            <strong>Binnen 24 Stunden</strong>: eine Erstmeldung mit einer
            ersten Einschätzung, ob ein rechtswidriger oder böswilliger Akt
            vorliegt und ob grenzüberschreitende Auswirkungen möglich sind.
          </LI>
          <LI>
            <strong>Binnen 72 Stunden</strong>: eine vollständige Meldung mit
            einer ersten Bewertung von Schweregrad und Auswirkungen sowie,
            soweit vorhanden, Kompromittierungsindikatoren (IOCs).
          </LI>
          <LI>
            <strong>Binnen eines Monats</strong>: ein Abschlussbericht mit
            detaillierter Beschreibung des Vorfalls, der mutmaßlichen
            Ursache, der ergriffenen Maßnahmen und etwaiger
            grenzüberschreitender Auswirkungen.
          </LI>
        </OL>
        <P>
          Diese Fristen sind kurz. Wer erst nach einem Vorfall anfängt, Logs
          zu sammeln, verpasst sie: Binnen 24 Stunden etwas Belastbares über
          böswilliges Handeln zu sagen setzt voraus, dass die relevanten Logs
          bereits vorliegen, durchsuchbar sind, und dass die Detektion den
          Vorfall überhaupt bemerkt hat.
        </P>
      </GuideSection>

      <GuideSection id="aufsicht-und-bussgelder" title="Aufsicht, Bußgelder und Haftung">
        <P>
          Zentrale Aufsichts- und Meldebehörde ist das{' '}
          <strong>Bundesamt für Sicherheit in der Informationstechnik
          (BSI)</strong>. Besonders wichtige Einrichtungen unterliegen
          proaktiver Aufsicht (Nachweise und Prüfungen), wichtige
          Einrichtungen reaktiver Aufsicht (nach Hinweisen oder Vorfällen).
        </P>
        <P>Die Bußgelder liegen auf DSGVO-Niveau:</P>
        <UL>
          <LI>
            besonders wichtige Einrichtungen: bis zu{' '}
            <strong>10 Millionen Euro oder 2%</strong> des weltweiten
            Jahresumsatzes;
          </LI>
          <LI>
            wichtige Einrichtungen: bis zu{' '}
            <strong>7 Millionen Euro oder 1,4%</strong> des weltweiten
            Jahresumsatzes.
          </LI>
        </UL>
        <P>
          Daneben kann die Aufsicht verbindliche Anweisungen erteilen; die
          Geschäftsleitung haftet bei grober Verletzung ihrer Billigungs- und
          Überwachungspflichten persönlich.
        </P>
      </GuideSection>

      <GuideSection id="logging-und-monitoring" title="Was das konkret für Logging und Monitoring bedeutet">
        <P>
          Logging und Monitoring tauchen in fast jeder Pflicht auf: Ohne die
          richtigen Logs können Sie keinen Vorfall erkennen, keine
          24-Stunden-Meldung begründen und keinen Abschlussbericht schreiben.
          Praktisch heißt das:
        </P>
        <H3>Logs zentralisieren</H3>
        <P>
          Logs von Servern, Netzwerkgeräten, Identity-Providern,
          Cloud-Diensten und Anwendungen gehören in ein durchsuchbares
          System. Einen Vorfall über zehn Einzelsysteme hinweg zu
          rekonstruieren gelingt nicht binnen 72 Stunden.
        </P>
        <H3>Lang genug aufbewahren, nachweisbar</H3>
        <P>
          Das Gesetz nennt keine feste Frist; der Maßstab ist, dass Sie
          Vorfälle untersuchen und melden können. Üblich sind 6 bis 18
          Monate, mit einer durchsuchbaren Hot-Ebene und einem günstigeren
          Archiv. Dokumentieren Sie Ihre Entscheidung und Begründung, genau
          danach fragt eine Aufsichtsbehörde.
        </P>
        <H3>In Echtzeit überwachen, nicht rückwirkend</H3>
        <P>
          Die Meldefristen setzen voraus, dass Sie Vorfälle sehen, während
          sie passieren. Das verlangt Detektionsregeln auf Ihren Logs
          (fehlgeschlagene Anmeldungen, ungewöhnliches Verhalten, verdächtige
          Netzwerkverbindungen) und Alarmierung, die einen Menschen rechtzeitig
          erreicht, ohne im Fehlalarm unterzugehen. Für KRITIS-Betreiber ist
          genau das mit der Pflicht zu Systemen zur Angriffserkennung schon
          heute ausdrücklich vorgeschrieben.
        </P>
        <H3>Einen Audit-Trail führen</H3>
        <P>
          Wer hat wann was getan, auch in Ihrer Logging-Plattform selbst? Ein
          manipulationssicherer Audit-Trail stützt sowohl Ihre Meldungen als
          auch Ihre Rechenschaft gegenüber der Aufsicht.
        </P>
        <P>
          Das ist die Seite, die LogPulse abdeckt: zentralisiertes
          Log-Management mit konfigurierbarer Aufbewahrung, über 50
          eingebaute Detektionen mit MITRE-ATT&amp;CK-Zuordnung,
          risikobasierte Alarmierung, die eine Handvoll echter Vorfälle
          liefert statt tausender Meldungen, und Compliance-Berichte, die
          Detektionen und Daten NIS2-Anforderungen zuordnen. Alle Daten
          bleiben in der EU (GCP Amsterdam), was Verarbeitungsverträge und
          die Rechenschaft gegenüber der Aufsicht erheblich vereinfacht.
        </P>
      </GuideSection>

      <GuideSection id="schritt-fuer-schritt" title="Schritt für Schritt: so gehen Sie es an">
        <OL>
          <LI>
            <strong>Betroffenheit klären</strong> mit der offiziellen
            BSI-Betroffenheitsprüfung, einschließlich der Kategorie.
          </LI>
          <LI>
            <strong>Geschäftsleitung einbinden</strong>: Sie trägt die
            persönliche Verantwortung und muss den Ansatz billigen.
          </LI>
          <LI>
            <strong>Risikoanalyse durchführen</strong> und kritische Systeme
            und Lieferanten identifizieren.
          </LI>
          <LI>
            <strong>Logging zentralisieren und Detektion aufbauen</strong>:
            das Fundament sowohl der Risikomanagement- als auch der
            Meldepflichten.
          </LI>
          <LI>
            <strong>Den Meldeprozess üben</strong>: Wer informiert binnen 24
            Stunden das BSI, und mit welchen Informationen?
          </LI>
          <LI>
            <strong>Alles dokumentieren</strong>: Maßnahmen, Entscheidungen,
            Aufbewahrungsfristen, Übungen. Compliance, die Sie nicht belegen
            können, existiert für eine Aufsichtsbehörde nicht.
          </LI>
        </OL>
        <P>
          Mehr dazu, wie LogPulse die Logging-, Detektions- und
          Berichtsseite abdeckt:{' '}
          <GuideLink href="/compliance/nis2">NIS2-Compliance mit LogPulse</GuideLink>
          , oder starten Sie kostenlos und richten Sie Ihre ersten
          Detektionen noch heute ein.
        </P>
      </GuideSection>
    </>
  );
}
